// @flow
import * as React from 'react'
import tw from 'tailwind-styled-components'
import Icons from 'ui/Icons'

export type Props = {
  id?: string,
  /**
   * Indicates if the component is active
   */
  $active?: boolean,
  /**
   * If true, the component will be displayed as a block element
   */
  $block?: boolean,
  /**
   * If true, the component will have a compact style
   */
  $compact?: boolean,
  /**
   * If true, the component will have a danger style
   */
  $danger?: boolean,
  /**
   * If true, the component will have a soft danger style
   */
  $softDanger?: boolean,
  /**
   * If true, the component will not have a border
   */
  $noBorder?: boolean,
  /**
   * If true, the component will not have an outline
   */
  $noOutline?: boolean,
  /**
   * If true, the component will not have margin for SVG elements
   */
  $noSvgMargin?: boolean,
  /**
   * If true, the component will not have padding
   */
  $noPadding?: boolean,
  /**
   * If true, the component will have a primary style
   */
  $primary?: boolean,
  /**
   * If true, the component will have a quarter style
   */
  $quarter?: boolean,
  /**
   * If true, the component will be rectangular
   */
  $rectangular?: boolean,
  /**
   * Specifies the width of the component
   */
  $width?: number,
  /**
   * If true, the component will have a plain style
   */
  $plain?: boolean,
  /**
   * The data-cy attribute used for testing
   */
  dataCy?: string,
  /**
   * The content to be displayed inside the component
   */
  children: React.Node,
  /**
   * If true, the component will be disabled
   */
  disabled?: boolean,
  /**
   * The icon to be displayed inside the component
   */
  icon?: React.Node,
  /**
   * The position of the icon, either 'left' or 'right'
   */
  iconPosition?: 'left' | 'right',
  /**
   * If true, the component will show a loading indicator
   */
  loading?: boolean,
  /**
   * The function to call when the component is clicked
   */
  onClick?: () => void | Promise<void> | null,
  /**
   * Specifies the className of the component
   */
  className?: string,
  type?: 'button' | 'submit' | 'reset',
}

const primaryStyles = `
  active:bg-blue-700
  bg-blue-600
  disabled:opacity-50
  focus:border-blue-700
  hover:bg-blue-500
  text-white
`

const dangerStyles = `
  bg-red-600
  hover:bg-red-500
  focus:border-red-700
  focus:shadow-outline-red
  active:bg-red-700
  text-white
`

const softDangerStyles = `
  bg-red-100
  hover:bg-red-200
  focus:border-red-300
  focus:shadow-outline-red
  active:bg-red-300
  text-red-700
`

function getPlainStyles(props: Props) {
  return `
    p-0
    border-0
    hover:bg-transparent
    bg-transparent
    text-gray-500

    ${
      props.$primary
        ? `
      text-blue-700
      hover:text-blue-500
      focus:shadow-none
    `
        : ''
    }

    ${
      props.$danger
        ? `
      text-red-500
      hover:text-red-500
      focus:shadow-none
    `
        : ''
    }
  `
}

const StyledButton: React.ComponentType<Props> = tw.button`
  active:bg-gray-200
  disabled:opacity-50
  disabled:cursor-not-allowed
  duration-150
  ease-in-out
  focus:border-gray-100
  focus:outline-none
  font-medium
  hover:bg-dark-10
  rounded-md
  text-gray-500
  transition
  flex
  items-center
  justify-center

  ${(props: Props) => props.$primary && primaryStyles}

  ${(props: Props) => props.$danger && dangerStyles}

  ${(props: Props) => props.$softDanger && softDangerStyles}

  ${(props: Props) => {
    if (props.$noBorder) return ''

    return 'border'
  }}

  ${(props: Props) => {
    if (props.$noOutline) return ''

    return 'focus:shadow-outline-blue'
  }}


  ${(props: Props) => {
    if (props.$compact) {
      return `
        px-2
        py-1
      `
    }

    return `
      px-6
      py-2
    `
  }}

  ${(props: Props) => props.$block && 'w-full'}

  ${(props: Props) => props.$quarter && 'w-1/4'}

  ${(props: Props) => props.$rectangular && 'rounded-none'}

  ${(props: Props) => props.$noPadding && 'p-0'}

  ${(props: Props) => props.$plain && getPlainStyles(props)}

  ${(props: Props) =>
    props.$active &&
    `
    bg-dark-10
  `}
`

/**
 * Primary UI component for user interaction
 */
function Button({
  loading,
  children,
  disabled,
  icon,
  iconPosition = 'left',
  onClick,
  dataCy,
  type = 'button',
  ...rest
}: Props): React.Node {
  return (
    <StyledButton
      {...rest}
      data-cy={dataCy}
      disabled={loading || disabled}
      onClick={onClick}
      type={type ?? 'button'}
    >
      {loading && <Icons.Spinner />}

      {!loading && icon && iconPosition === 'left' && icon}
      {children}
      {!loading && icon && iconPosition === 'right' && icon}
    </StyledButton>
  )
}

export default Button
